import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, MapPin, Clock, ArrowUpRight, Sparkles, HeartHandshake } from 'lucide-react';
import { careersData } from '../mock';

const Careers = () => {
  return (
    <section id="careers" className="py-24 relative overflow-hidden bg-white">
      {/* Background Glow */}
      <div className="absolute top-1/3 right-0 w-96 h-96 bg-purple-200/40 blur-[140px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider mb-3"
          >
            <Sparkles size={14} className="text-purple-600" />
            <span>Join Our Team</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-5xl font-extrabold font-display text-slate-900 tracking-tight mb-4"
          >
            Build the Future of <span className="text-gradient">3D Web & Mobile</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-slate-600 text-base leading-relaxed font-normal"
          >
            We are looking for passionate developers, designers, and innovators to create next-generation web applications.
          </motion.p>
        </div>

        {/* Benefits & Culture Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {[
            { title: 'Remote-First Culture', desc: 'Work from anywhere with flexible working hours.', icon: MapPin },
            { title: 'Competitive Package', desc: 'Top-tier compensation, bonuses & equipment allowances.', icon: HeartHandshake },
            { title: 'Cutting-Edge Tech Stack', desc: 'Build with React, Three.js, WebGL & modern frameworks.', icon: Briefcase },
          ].map((benefit, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="p-6 rounded-2xl glass-card flex items-start space-x-4 border border-slate-200"
            >
              <div className="w-12 h-12 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
                <benefit.icon size={22} />
              </div>
              <div>
                <h4 className="text-base font-bold font-display text-slate-900 mb-1">{benefit.title}</h4>
                <p className="text-xs text-slate-600 font-normal leading-relaxed">{benefit.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Open Positions List */}
        <div className="space-y-6 max-w-5xl mx-auto">
          {careersData.map((job, index) => (
            <motion.div
              key={job.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="p-8 rounded-3xl glass-card border border-slate-200 hover:border-purple-300 transition-all flex flex-col md:flex-row md:items-center justify-between gap-6 group"
            >
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-purple-100 text-purple-700">
                    {job.department}
                  </span>
                  <span className="px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600">
                    {job.type}
                  </span>
                </div>

                <h3 className="text-2xl font-bold font-display text-slate-900 group-hover:text-purple-600 transition-colors">
                  {job.role}
                </h3>

                <p className="text-slate-600 text-sm font-normal max-w-2xl leading-relaxed">
                  {job.description}
                </p>

                <div className="flex items-center space-x-4 text-xs text-slate-500 font-medium pt-1">
                  <span className="flex items-center"><MapPin size={14} className="mr-1 text-purple-600" />{job.location}</span>
                  <span className="flex items-center"><Clock size={14} className="mr-1 text-purple-600" />{job.type}</span>
                </div>
              </div>

              <div className="shrink-0">
                <a
                  href="#contact"
                  className="px-6 py-3 rounded-xl bg-slate-900 hover:bg-purple-600 text-white font-bold text-xs flex items-center space-x-2 transition-all duration-300 shadow-md group-hover:scale-105"
                >
                  <span>Apply Now</span>
                  <ArrowUpRight size={16} />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Careers;
